// SETTINGS (redesign stage 1): only the preferences GroundZero really has today - theme and the "Show advanced"
// detail level, both kept in this browser. Everything the terminal will have later is listed as not available yet;
// trade, scanner and risk settings stay on their own pages (and are linked).
import React from "react";
import { Link } from "react-router-dom";
import { Cog } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAdvanced } from "@/lib/advanced";

const ROW = "flex items-center justify-between gap-4 py-2.5 border-t border-[var(--term-border)] first:border-t-0";

export default function SettingsPage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [advanced, setAdvanced] = useAdvanced();
  return (
    <div className="p-4 md:p-6 max-w-3xl space-y-4" data-testid="settings-page">
      <h1 className="text-lg font-bold flex items-center gap-2"><Cog className="h-5 w-5" />Settings</h1>
      <section className="term-panel rounded-md border border-[var(--term-border)] p-4">
        <h2 className="text-[10px] font-bold uppercase tracking-wider text-[var(--term-secondary)] mb-1">Display (this browser)</h2>
        <div className={ROW}>
          <div><div className="text-sm font-semibold">Theme</div><div className="text-[11px] text-[var(--term-muted)]">Light or dark terminal</div></div>
          <div className="inline-flex rounded-md border border-[var(--term-border-strong)] p-0.5 text-xs" data-testid="settings-theme">
            {["light", "dark"].map((t) => <button key={t} onClick={() => setTheme(t)} aria-pressed={resolvedTheme === t}
              className={`h-7 px-3 rounded-[5px] capitalize ${resolvedTheme === t ? "bg-[#2563EB] text-white font-bold" : "text-[var(--term-secondary)]"}`}>{t}</button>)}
          </div>
        </div>
        <div className={ROW}>
          <div><div className="text-sm font-semibold">Show advanced detail</div><div className="text-[11px] text-[var(--term-muted)]">Raw status codes, diagnostics and engineering fields on the trading screens</div></div>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} data-testid="settings-advanced" />{advanced ? "On" : "Off"}</label>
        </div>
        <div className={ROW}>
          <div><div className="text-sm font-semibold">Menu</div><div className="text-[11px] text-[var(--term-muted)]">Collapse or expand with the button at the top of the menu (remembered here)</div></div>
        </div>
      </section>
      <section className="term-panel rounded-md border border-[var(--term-border)] p-4 space-y-1">
        <h2 className="text-[10px] font-bold uppercase tracking-wider text-[var(--term-secondary)]">Where other settings live</h2>
        <ul className="text-xs space-y-1">
          <li><Link className="text-[#2563EB] hover:underline" to="/options/settings">Scanner Settings</Link> — option scanner filters and defaults</li>
          <li>Each trade's target, stop and auto square-off — its <b>Settings</b> action (Common Trade Panel or its trade page)</li>
          <li><Link className="text-[#2563EB] hover:underline" to="/account">My Account</Link> — profile, password, sessions, trading access</li>
          <li><Link className="text-[#2563EB] hover:underline" to="/broker">Broker Session</Link> — Zerodha connection</li>
        </ul>
      </section>
      <section className="term-panel rounded-md border border-dashed border-[var(--term-border-strong)] p-4 text-xs text-[var(--term-muted)]" data-testid="settings-unavailable">
        Not available yet: notification / alert preferences, default order settings, layout and column presets.
      </section>
    </div>
  );
}
